import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { chatApi, type ChatMessage, type ChatSession } from '@/api/chat';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { humanizeError } from '@/lib/errors';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ChatAttachment } from '@/components/chat/ChatAttachment';
import { StaffBubble } from '@/components/chat/StaffBubble';
import { chatDayOf } from '@/lib/chatDay';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MBlock, MEmpty, MRow, MSection, MTag, MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useBackHandler } from '@/lib/backButton';
import { cn } from '@/lib/utils';

export function AdminChatMonitoringPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeRoom, setActiveRoom] = useState<string | null>(null);

  const confirmDeleteMessage = async () => await ask({ message: t('adminChat.confirmDeleteMessage'), danger: true });

  // Sessions list — when a query is present the backend returns only sessions
  // that contain a matching message (TZ 3.7 chat monitoring search).
  const sessions = useQuery({
    queryKey: ['admin', 'chats', search],
    queryFn: async () => {
      const { data } = await api.get('/admin/chats', { params: { q: search } });
      return ((data as { items?: ChatSession[] }).items ?? data ?? []) as ChatSession[];
    },
  });

  // Global message search (dedicated endpoint). Only runs when typing a query.
  const messageSearch = useQuery({
    queryKey: ['admin', 'chat-search', search],
    queryFn: () => chatApi.searchMessages(search),
    enabled: search.trim().length > 0,
  });

  const history = useQuery({
    queryKey: ['admin', 'chat-history', activeRoom],
    queryFn: () => chatApi.getHistory(activeRoom!),
    enabled: !!activeRoom,
  });

  const deleteMessage = useMutation({
    mutationFn: async (id: string) => api.delete(`/admin/chats/messages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'chat-history'] });
      qc.invalidateQueries({ queryKey: ['admin', 'chat-search'] });
    },
  });

  const searching = search.trim().length > 0;

  const isMobile = useIsMobile();
  // Сообщение, у которого открыт лист «⋯» (телефон).
  const [sheetFor, setSheetFor] = useState<ChatMessage | null>(null);
  const backToList = useCallback(() => {
    setActiveRoom(null);
    return true;
  }, []);
  useBackHandler(backToList, isMobile && !!activeRoom);

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.24) ─────────────────────────
  // Два экрана: список (поиск, найденные, сессии) и диалог только для чтения. Удаление — через лист «⋯» и то же
  // подтверждение, что на компьютере. До 13.10.2026 страница на 390 была шире экрана на ~50 px.
  if (isMobile) {
    const sheet = (
      <ActionSheet
        open={!!sheetFor}
        onClose={() => setSheetFor(null)}
        actions={
          sheetFor
            ? [
                ...(!activeRoom
                  ? [{ label: t('adminChat.openChat'), icon: 'forum', onSelect: () => setActiveRoom(sheetFor.room_id) }]
                  : []),
                {
                  label: t('common.delete'),
                  icon: 'delete',
                  danger: true,
                  onSelect: async () => {
                    if (await confirmDeleteMessage()) deleteMessage.mutate(sheetFor.id);
                  },
                },
              ]
            : []
        }
      />
    );
    const moreButton = (m: ChatMessage) => (
      <button
        type="button"
        onClick={() => setSheetFor(m)}
        aria-label={t('nav.more')}
        className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
      >
        <span className="material-symbols-outlined text-[20px]">more_horiz</span>
      </button>
    );
    const deleteError = deleteMessage.error && (
      <p className="px-0.5 text-[13px] text-error">{humanizeError(deleteMessage.error)}</p>
    );

    if (activeRoom) {
      const session = sessions.data?.find((s) => s.id === activeRoom);
      const msgs = history.data ?? [];
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5 pb-1 pt-1.5">
            <button
              type="button"
              onClick={() => setActiveRoom(null)}
              aria-label={t('adminChat.title')}
              className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[16px] font-bold leading-tight text-on-surface">{t('adminChat.messages')}</div>
              {session && (
                <div className="text-[11.5px] text-on-surface-variant">
                  {t('adminChat.sessionLabel', {
                    studentId: session.student_name || session.student_id.slice(0, 6),
                    curatorId: session.curator_name || (session.curator_id ?? '').slice(0, 6),
                  })}
                </div>
              )}
            </div>
          </div>
          {deleteError}
          {history.isLoading ? (
            <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
          ) : history.isError ? (
            <p className="px-0.5 text-[13px] text-error">{humanizeError(history.error)}</p>
          ) : (
            <div data-chat-feed className="flex flex-col gap-2.5">
              {msgs.map((m, i) => (
                <StaffBubble
                  key={m.id}
                  msg={m}
                  // Только чтение: справа — куратор, слева — ученик, как куратор видит свой диалог.
                  mine={m.sender_role === 'curator'}
                  showDay={chatDayOf(m) !== chatDayOf(msgs[i - 1])}
                  aside={moreButton(m)}
                />
              ))}
            </div>
          )}
          {sheet}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <MTitle title={t('adminChat.title')} subtitle={t('adminChat.subtitle')} />
        <label className="flex items-center gap-2 rounded-full bg-surface-container-low px-4">
          <span className="material-symbols-outlined text-[18px] text-on-surface-variant">search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('adminChat.searchPlaceholder')}
            className="h-11 w-full bg-transparent text-[14px] text-on-surface outline-none placeholder:text-on-surface-variant"
          />
        </label>
        {deleteError}
        {searching && (
          <>
            <MSection title={t('adminChat.foundMessages')} />
            {messageSearch.isLoading ? (
              <p className="px-0.5 text-[13px] text-on-surface-variant">{t('adminChat.searching')}</p>
            ) : messageSearch.data && messageSearch.data.length > 0 ? (
              <MBlock>
                {messageSearch.data.map((m, i, arr) => (
                  <div key={m.id} className={cn('flex items-start gap-2 py-3', i < arr.length - 1 && 'border-b border-outline')}>
                    <button
                      type="button"
                      onClick={() => setActiveRoom(m.room_id)}
                      className="min-w-0 flex-1 text-left text-[13px] text-on-surface"
                    >
                      <MTag tone="muted">{m.sender_role}</MTag>
                      <div className="mt-1">
                        <MessageBody msg={m} />
                      </div>
                    </button>
                    {moreButton(m)}
                  </div>
                ))}
              </MBlock>
            ) : (
              <p className="px-0.5 text-[13px] text-on-surface-variant">{t('adminChat.nothingFound')}</p>
            )}
          </>
        )}
        {sessions.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : sessions.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(sessions.error)}</p>
        ) : !sessions.data?.length ? (
          <MEmpty icon="forum" title={searching ? t('adminChat.noChatsFound') : t('adminChat.noChatsYet')} />
        ) : (
          <MBlock>
            {sessions.data.map((s, i, arr) => (
              <MRow
                key={s.id}
                wrap
                onClick={() => setActiveRoom(s.id)}
                title={t('adminChat.sessionLabel', {
                  studentId: s.student_name || s.student_id.slice(0, 6),
                  curatorId: s.curator_name || (s.curator_id ?? '').slice(0, 6),
                })}
                trailing={
                  <MTag tone={s.status === 'active' ? 'info' : 'muted'}>
                    {s.status === 'active' ? t('adminChat.statusActive') : t('adminChat.statusClosed')}
                  </MTag>
                }
                last={i === arr.length - 1}
              />
            ))}
          </MBlock>
        )}
        {sheet}
      </div>
    );
  }

  return (
    <>
      <PageHeader title={t('adminChat.title')} subtitle={t('adminChat.subtitle')} />

      <Input
        placeholder={t('adminChat.searchPlaceholder')}
        leftIcon={<span className="material-symbols-outlined">search</span>}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 max-w-md"
      />

      {deleteMessage.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{humanizeError(deleteMessage.error)}</Card>
      )}

      {searching && (
        <Card className="mb-4">
          <CardTitle>{t('adminChat.foundMessages')}</CardTitle>
          {messageSearch.isLoading ? (
            <p className="mt-2 text-sm text-on-surface-variant">{t('adminChat.searching')}</p>
          ) : messageSearch.data && messageSearch.data.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {messageSearch.data.map((m) => (
                <li
                  key={m.id}
                  className="flex items-start gap-3 rounded-button bg-surface-container-low p-3 text-sm"
                >
                  <Pill variant="neutral">{m.sender_role}</Pill>
                  <div className="flex-1">
                    <MessageBody msg={m} />
                    <button
                      className="mt-1 text-xs text-primary underline"
                      onClick={() => setActiveRoom(m.room_id)}
                    >
                      {t('adminChat.openChat')}
                    </button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={async () => { if (await confirmDeleteMessage()) deleteMessage.mutate(m.id); }} title={t('common.delete')}>
                    <span className="material-symbols-outlined">delete</span>
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-on-surface-variant">{t('adminChat.nothingFound')}</p>
          )}
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-[300px_1fr]">
        <Card className="p-0">
          {sessions.isLoading ? (
            <p className="p-4">{t('common.loading')}</p>
          ) : sessions.error ? (
            <p className="p-4 text-sm text-error">{humanizeError(sessions.error)}</p>
          ) : (
            <ul>
              {sessions.data?.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => setActiveRoom(s.id)}
                    className={`flex w-full items-center justify-between p-3 text-left text-sm transition hover:bg-surface-container-low ${activeRoom === s.id ? 'bg-primary-fixed' : ''}`}
                  >
                    <span className="min-w-0 truncate">
                      {t('adminChat.sessionLabel', {
                        studentId: s.student_name || s.student_id.slice(0, 6),
                        curatorId: s.curator_name || (s.curator_id ?? '').slice(0, 6),
                      })}
                    </span>
                    <Pill variant={s.status === 'active' ? 'success' : 'neutral'}>
                      {s.status === 'active' ? t('adminChat.statusActive') : t('adminChat.statusClosed')}
                    </Pill>
                  </button>
                </li>
              ))}
              {sessions.data?.length === 0 && (
                <li className="p-4 text-sm text-on-surface-variant">
                  {searching ? t('adminChat.noChatsFound') : t('adminChat.noChatsYet')}
                </li>
              )}
            </ul>
          )}
        </Card>

        <Card>
          {activeRoom ? (
            <>
              <CardTitle>{t('adminChat.messages')}</CardTitle>
              <ul className="mt-4 space-y-2">
                {history.data?.map((m: ChatMessage) => (
                  <li key={m.id} className="flex items-start gap-3 rounded-button bg-surface-container-low p-3 text-sm">
                    <Pill variant="neutral">{m.sender_role}</Pill>
                    <div className="flex-1">
                      <MessageBody msg={m} />
                      <p className="mt-1 text-xs text-on-surface-variant">{new Date(m.created_at).toLocaleString('ru-RU')}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={async () => { if (await confirmDeleteMessage()) deleteMessage.mutate(m.id); }} title={t('common.delete')}>
                      <span className="material-symbols-outlined">delete</span>
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="py-8 text-center text-on-surface-variant">
              {t('adminChat.selectChatHint')}
            </p>
          )}
        </Card>
      </div>
    </>
  );
}

/**
 * Сообщение в ленте админа. Вложения — тем же компонентом, что у куратора (техдолг 389, 12.10.2026): до этого видео
 * и PDF были голой ссылкой, битый файл — пустой рамкой, а `href` брался как есть — `javascript:` от ученика
 * выполнился бы в сессии админа.
 */
function MessageBody({ msg }: { msg: ChatMessage }) {
  return (
    <div>
      {msg.attachments?.map((a, i) => <ChatAttachment key={a.id ?? `${a.url}-${i}`} attachment={a} />)}
      {msg.content && <p className="whitespace-pre-wrap break-words">{msg.content}</p>}
    </div>
  );
}
